// Free-cover / MCP continue: seed the picture-book flow from the visitor's cover (photo, title, story idea).
// The photo becomes an in-memory data URL; it is never written to localStorage (a child's photo).
import { classifySeedResponse, coverStorySeed } from '@/lib/free-cover/continuation';

export interface CoverSeed {
  photoDataUrl: string;
  /** The same photo as a File, for the Photo step (decoding a data: URL with fetch is blocked by the CSP). */
  photoFile: File;
  title: string | null;
  idea: string | null;
  templateId: string | null;
  /**
   * The cover holds a stored photo consent (R5): the photo steps and the book inherit it, no new tick. False (also
   * when the server does not say): the Photo step shows the box and the photo waits for the tick.
   */
  photoConsented: boolean;
}

export type CoverSeedResult =
  | { ok: true; seed: CoverSeed }
  | { ok: false; reason: 'expired' | 'denied' | 'error'; message?: string };

/** Type guard for the failure branch (the project is non-strict, so `ok` alone does not narrow the union). */
export function seedFailed(r: CoverSeedResult): r is Extract<CoverSeedResult, { ok: false }> {
  return r.ok === false;
}

/** Shown when the seed route gives no message of its own. */
export const PHOTO_EXPIRED_FALLBACK = 'Your photo has expired - please upload it again to continue.';

async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${blob.type || 'image/jpeg'};base64,${btoa(bin)}`;
}

/**
 * GET the owner-only seed (the owner cookie travels with the same-origin fetch; `t` only when a caller still has
 * one), then the 15-minute signed photo. 410 -> expired (with the server's message), 403 -> denied, anything else -> error.
 */
export async function loadCoverSeed(
  slug: string,
  t: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<CoverSeedResult> {
  try {
    const qs = `slug=${encodeURIComponent(slug)}${t ? `&t=${encodeURIComponent(t)}` : ''}`;
    const res = await fetchImpl(`/api/free-cover/seed?${qs}`);
    const body = await res.json().catch(() => null);
    const outcome = classifySeedResponse(res.status, body);
    if (outcome === 'expired') {
      return { ok: false, reason: 'expired', message: typeof body?.error === 'string' && body.error ? body.error : PHOTO_EXPIRED_FALLBACK };
    }
    if (outcome === 'forbidden') return { ok: false, reason: 'denied' };
    if (outcome !== 'ok') return { ok: false, reason: 'error' };

    const photoRes = await fetchImpl(body.photoUrl);
    if (!photoRes.ok) return { ok: false, reason: 'error' };
    const blob = await photoRes.blob();
    const photoDataUrl = await blobToDataUrl(blob);
    if (!photoDataUrl.startsWith('data:')) return { ok: false, reason: 'error' };
    const photoFile = new File([blob], 'cover-photo.jpg', { type: blob.type || 'image/jpeg' });

    const story = coverStorySeed(typeof body.themeId === 'string' ? body.themeId : null);
    return {
      ok: true,
      seed: {
        photoDataUrl,
        photoFile,
        title: typeof body.title === 'string' && body.title.trim() ? body.title.trim() : null,
        idea: story?.storyIdea ?? null,
        templateId: story?.template?.id ?? null,
        photoConsented: body.photoConsented === true,
      },
    };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/**
 * The Your-book value for a seeded flow. Title and idea are OUR suggestions (source 'suggestion', so the
 * non-English confirm of P2-R10 applies: suggestionSource 'fallback' marks them as English text). `coverSeed`
 * stops the first-visit suggestion call from replacing them.
 */
export function applyCoverSeed<
  V extends {
    title: string;
    idea: string;
    style: string;
    source: Record<'style' | 'title' | 'idea', 'suggestion' | 'template' | 'parent'>;
    suggested?: { style: any; title: string; idea: string } | null;
    suggestionSource?: 'llm' | 'fallback';
    coverSeed?: { title: string | null; idea: string | null };
  },
>(value: V, seed: Pick<CoverSeed, 'title' | 'idea'>): V {
  if (!seed.title && !seed.idea) return value;
  const title = seed.title ?? value.title;
  const idea = seed.idea ?? value.idea;
  return {
    ...value,
    title,
    idea,
    source: { ...value.source, title: 'suggestion', idea: 'suggestion' },
    suggested: { style: value.style, title: seed.title ?? '', idea: seed.idea ?? '' },
    suggestionSource: 'fallback',
    coverSeed: { title: seed.title, idea: seed.idea },
  };
}
